/* WeQuote Dev Board — Needs Review (renderReview / reviewRowHtml /
 * reviewReadHtml, faithful port). Master–detail intake queue; the read pane
 * carries the intake-alert line, the resolve bar (Add to dev / Merge /
 * Archive), intake line + similar-features count, customer + design-required,
 * checklist, tabs and source. The "Simulate support-chat request" demo
 * button was removed (Lee, 29 Aug — out of the testing phase); the real
 * support-chat intake pathway arrives post-parity. */
import React, { useEffect, useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import * as M from '../core/mutations'
import type { Feature } from '../core/types'
import { custLabel } from '../core/logic'
import { reviewIntakeLine, similarCount } from '../core/review'
import { ChecklistBlock, DetailTabs, SourceLine } from './parts'
import { IcCheck, IcMerge, IcArchive, IcMail, IcPlus, IcPencil } from '../icons'

export const ReviewResolveBar: React.FC<{ id: number; top?: boolean }> = ({ id, top }) => {
  const { resolveAddToDev, openSubModal } = useDevBoard()
  return (
    <div className={'rv-actions' + (top ? ' rv-actions-top' : '')}>
      <button className="rv-act add" onClick={() => resolveAddToDev(id)}><IcCheck size={12} /> Add to list</button>
      <button className="rv-act merge" onClick={() => openSubModal({ kind: 'merge', id })}><IcMerge size={12} /> Merge</button>
      {!top && <button className="rv-act arch" onClick={() => openSubModal({ kind: 'archive', id })}><IcArchive size={12} /> Archive</button>}
    </div>
  )
}

const ReviewRow: React.FC<{ feat: Feature; all: Feature[]; selected: boolean; onClick: () => void }> = ({ feat, all, selected, onClick }) => {
  const sim = similarCount(feat, all)
  return (
    <div className={'dv-row' + (selected ? ' selected' : '')} onClick={onClick}>
      <div className="dv-row-top"><span className="rv-tag">NEW</span><span className="dv-row-title">{feat.title}</span></div>
      <div className="rv-when">{reviewIntakeLine(feat)}</div>
      <div className="rv-sim">{sim} similar feature{sim === 1 ? '' : 's'}</div>
      {feat.notes && <div className="rv-preview">{feat.notes}</div>}
    </div>
  )
}

const ReviewRead: React.FC<{ feat: Feature; all: Feature[] }> = ({ feat, all }) => {
  const { mutate, openSubModal, openCardModal } = useDevBoard()
  const sim = similarCount(feat, all)
  return (
    <>
      {feat.alertEmailedTo && (
        <div className="intake-alert"><IcMail size={12} /> Alert email sent to <a href={'mailto:' + feat.alertEmailedTo}>{feat.alertEmailedTo}</a> when this landed in Needs Review.</div>
      )}
      <ReviewResolveBar id={feat.id} />
      <div className="dv-d-top">
        <div className="dv-d-heading"><h2 className="dv-d-title">{feat.title}</h2></div>
        <div className="dv-d-actions">
          <button className="dv-quick-btn req" onClick={() => openSubModal({ kind: 'addRequest', id: feat.id })}><IcPlus size={12} /> Request / nudge</button>
          <button className="dv-edit-btn" onClick={() => openCardModal(feat.id)}><IcPencil size={12} /> Edit details</button>
        </div>
      </div>
      <div className="dv-d-pills"><span className="status-pill s-needsreview">Needs Review</span></div>
      <div className="rv-intake">
        <div className="rv-intake-when">{reviewIntakeLine(feat)}</div>
        <div className="rv-intake-sim">{sim} similar feature{sim === 1 ? '' : 's'} — check before adding, in case it should be merged</div>
      </div>
      <div className="dv-d-meta">
        <div className="dv-d-cell"><span className="dv-d-k">Customer(s)</span><span className="dv-d-v">{custLabel(feat)}</span></div>
        <div className="dv-d-cell dv-d-design">
          <span className="dv-d-k">Design</span>
          <label className="rq-design"><input type="checkbox" checked={!!feat.requiresDesign} onChange={(e) => mutate((fs) => M.setRequiresDesign(fs, feat.id, e.target.checked))} /> Design required</label>
        </div>
      </div>
      <div className="dv-d-sec">
        <ChecklistBlock feat={feat} />
        <DetailTabs feat={feat} />
        <SourceLine feat={feat} />
      </div>
    </>
  )
}

export const ReviewView: React.FC = () => {
  const { cards } = useDevBoard()
  const items = cards.filter((f) => f.status === 'needsreview')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  useEffect(() => {
    if (selectedId !== null && !items.some((f) => f.id === selectedId)) setSelectedId(null)
    if (selectedId === null && items.length) setSelectedId(items[0].id)
  }, [items, selectedId])
  const sel = selectedId != null ? items.find((f) => f.id === selectedId) || null : null
  return (
    <div id="reviewView" style={{ padding: '16px 24px' }}>
      <div className="rv-topbar">
        <p className="rv-intro">New requests land here first. Review each one — add it to the dev list, merge it into an existing card, or archive it.</p>
      </div>
      <div className={'dv-wrap' + (sel ? ' has-selection' : '')}>
        <div className="dv-list">
          {items.length === 0
            ? <div className="dv-empty-list">Nothing to review right now. New requests will appear here.</div>
            : items.map((f) => <ReviewRow key={f.id} feat={f} all={cards} selected={f.id === selectedId} onClick={() => setSelectedId(f.id)} />)}
        </div>
        <div className="dv-detail">{sel && <ReviewRead feat={sel} all={cards} />}</div>
      </div>
    </div>
  )
}
